/**
 * Secret Management API Types
 *
 * Types for managing organization secrets (API keys, credentials, etc.)
 * for third-party systems like Valor Paytech.
 */

import { ThirdPartySystemId } from './types/third-party-system.enums';
import { CredentialType } from '@pillar-portal/database/dist/frontend';
import type { AuthorizableAction } from '@pillar-portal/database/dist/frontend';

export interface ValorCredentialsPublic {
    domain: string; // e.g. 'https://securelink-staging.valorpaytech.com:4430'
    epi: string; // e.g. '1234567890'
}
export type ValorCredentialsPublicFields = keyof ValorCredentialsPublic;

export interface ValorCredentialsSecret {
    appid: string;
    appkey: string;
    channel: string;
}
export type ValorCredentialsSecretFields = keyof ValorCredentialsSecret;

/**
 * Valor API credentials structure
 */
export interface ValorCredentials extends ValorCredentialsPublic, ValorCredentialsSecret {}

/**
 * Draft credential payload used while collecting incomplete credentials.
 */
export interface DraftValorCredentials {
    domain?: string;
    appid?: string;
    appkey?: string;
    epi?: string;
    channel?: string;
}

/**
 * Union type of all Valor credential field names (both public and secret)
 */
export type ValorCredentialsAllFields = ValorCredentialsPublicFields | ValorCredentialsSecretFields;

/**
 * Valor credential fields that are injected into Valor API requests
 * (These are typically omitted from request types since they're added by the gateway layer)
 */
export type ValorRequestAuthFields = 'appid' | 'appkey' | 'epi';

export { CredentialType };

export const CredentialTypeDisplayName: Record<CredentialType, string> = {
    [CredentialType.PHYSICAL_TERMINAL]: 'Physical Terminal',
    [CredentialType.VIRTUAL_TERMINAL]: 'Virtual Terminal',
};

/**
 * Union type for all credential types
 * Currently only Valor, will expand as needed
 */
export type ThirdPartyCredentials = ValorCredentials;
export type ThirdPartyCredentialsDraft = DraftValorCredentials;

/**
 * Credential type registry
 * Maps ThirdPartySystemId to credential structure and metadata
 */
export interface CredentialTypeDefinition {
    name: string; // Human-readable name
    publicFields: string[]; // Fields stored in cleartext for search (in public_fields JSONB)
    secretFields: string[]; // Fields stored encrypted (in encrypted_credentials)
    requiredFields: string[]; // All required fields
}

/**
 * Credential type registry
 * Maps ThirdPartySystemId to credential structure and metadata
 * Currently only supports Valor
 */
export const CredentialTypeRegistry: Partial<Record<ThirdPartySystemId, CredentialTypeDefinition>> =
    {
        [ThirdPartySystemId.VALOR_US]: {
            name: 'Valor Paytech',
            publicFields: ['domain', 'epi'], // Searchable fields
            secretFields: ['channel', 'appid', 'appkey'], // Encrypted fields
            requiredFields: ['domain', 'appid', 'appkey', 'epi', 'channel'],
        },
    };

/**
 * Organization secret (returned from API)
 */
export interface Credentials {
    credential_id: number;
    credential_uuid: string;
    organization_id: number;
    organization_uuid: string;
    system_id: ThirdPartySystemId;
    account_id: string; // Unique mnemonic id assigned by the client
    credential_type: CredentialType;
    public_fields: Record<string, unknown>; // Cleartext searchable fields
    masked_preview: string; // Masked credential preview for UI
    is_active: boolean;
    applies_to_children: boolean; // Applies to descendant orgs
}

/**
 * Organization secret with decrypted credentials (requires DISPLAY permission)
 */
export interface CredentialsWithCredentials extends Credentials {
    credentials: DraftValorCredentials; // Decrypted credentials may be incomplete drafts
}

export interface SecretUuidReference {
    credential_uuid: string;
}

export interface SecretOrganizationUuidReference {
    organization_uuid: string;
}

/**
 * List secrets response
 */
export interface SecretListItem extends SecretMetadataResponse {}

export interface ListSecretsResponse {
    secrets: SecretListItem[];
}

export interface ListSecretsForOrganizationQuery {
    actions?: AuthorizableAction[];
    credential_type?: CredentialType;
    terms?: string[];
}

export const CredentialUsageType = {
    LOGICAL_TERMINAL: 'logical_terminal',
    TERMINAL_DEVICE: 'terminal_device',
} as const;

export type CredentialUsageType = (typeof CredentialUsageType)[keyof typeof CredentialUsageType];

export interface LogicalTerminalUsageReference {
    usage_type: typeof CredentialUsageType.LOGICAL_TERMINAL;
    logical_terminal_uuid: string;
}

export interface TerminalDeviceUsageReference {
    usage_type: typeof CredentialUsageType.TERMINAL_DEVICE;
    terminal_device_uuid: string;
}

export type CredentialUsageReference =
    | LogicalTerminalUsageReference
    | TerminalDeviceUsageReference;

export type LogicalTerminalUsageListItem = LogicalTerminalUsageReference & {
    display_name: string;
};

export type TerminalDeviceUsageListItem = TerminalDeviceUsageReference & {
    display_name: string;
};

export type CredentialUsageListItem =
    | LogicalTerminalUsageListItem
    | TerminalDeviceUsageListItem;

export interface CredentialUsagesResponse {
    usages: CredentialUsageListItem[];
}

export interface SecretIdentityReference {
    credential_id: number;
    credential_uuid: string;
}

export interface SecretOrganizationReference {
    organization_id: number;
    organization_uuid: string;
}

export interface DisconnectCredentialUsagesRequest {
    usages: CredentialUsageReference[];
}

export interface DisconnectCredentialUsagesResponse {
    disconnected_count: number;
    remaining_usage_count: number;
    message: string;
}

/**
 * Create secret request
 */
export interface CreateSecretRequest {
    organization_uuid: string;
    system_id: ThirdPartySystemId;
    account_id: string;
    credential_type: CredentialType;
    credentials: DraftValorCredentials;
    credential_label?: string;
    applies_to_children?: boolean;
}

/**
 * Update secret request
 */
export interface UpdateSecretRequest {
    account_id?: string;
    credential_type?: CredentialType;
    credentials?: DraftValorCredentials;
    credential_label?: string;
    applies_to_children?: boolean;
    is_active?: boolean;
}

/**
 * Get secret response (with decrypted credentials)
 */
export interface GetSecretResponse extends SecretWithCredentialsResponse {}

export interface SecretWithCredentialsResponse extends CreateSecretResponse {
    credentials: DraftValorCredentials;
}

/**
 * Create secret response
 */
export interface CreateSecretResponse {
    credential_uuid: string;
    organization_uuid: string;
    system_id: ThirdPartySystemId;
    account_id: string;
    credential_type: CredentialType;
    public_fields: Record<string, unknown>;
    masked_preview: string;
    is_active: boolean;
    applies_to_children: boolean;
}

export interface SecretMetadataResponse extends CreateSecretResponse {}

/**
 * Update secret response
 */
export interface SaveSecretResponse extends CreateSecretResponse {}
